import React, { useState, useRef, useContext, useEffect } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import {
  FaShieldAlt,
  FaCog,
  FaUpload,
  FaPlus,
  FaTrash,
  FaTv,
  FaCheck,
  FaDownload,
  FaEye,
  FaEdit,
  FaCalendarAlt,
  FaFilter,
  FaChartBar,
  FaImage,
} from 'react-icons/fa'
import Bounce from 'react-activity/dist/Bounce'
import 'react-activity/dist/Bounce.css'
import { FaXmark } from 'react-icons/fa6'
import { getVpnImage } from '../utils/team'

const VPN = () => {
  const {
    isLoading,
    getBroadcasters,
    addBroadcaster,
    getVpns,
    addVpn,
    updateVpn,
    uploadFile,
    deleteBroadcaster,
    deleteVpn,
    getVpnTests,
    getVpnTest,
    addVpnTest,
    updateVpnTest,
    deleteVpnTest,
    getVpnTestStats,
    getDistinctMatchdays,
    getRecognizedCountries,
  } = useContext(AppContext)

  // State for broadcasters
  const [broadcasters, setBroadcasters] = useState([])
  const [newBroadcaster, setNewBroadcaster] = useState('')
  const [broadcasterFile, setBroadcasterFile] = useState(null)

  // State for VPNs
  const [vpns, setVpns] = useState([])
  const [newVpn, setNewVpn] = useState('')
  const [vpnDescription, setVpnDescription] = useState('')
  const [vpnFile, setVpnFile] = useState(null)
  const [vpnLogo, setVpnLogo] = useState(null)

  // State for file upload
  const [uploadFormat, setUploadFormat] = useState('excel') // 'excel' or 'pdf'

  // State for delete loading
  const [deletingBroadcasters, setDeletingBroadcasters] = useState({})
  const [deletingVpns, setDeletingVpns] = useState({})

  // State for add loading
  const [addingBroadcaster, setAddingBroadcaster] = useState(false)
  const [addingVpn, setAddingVpn] = useState(false)
  const [updatingVpn, setUpdatingVpn] = useState(false)

  // State for VPN tests
  const [vpnTests, setVpnTests] = useState([])
  const [vpnTestStats, setVpnTestStats] = useState({})
  const [showTestModal, setShowTestModal] = useState(false)
  const [editingTest, setEditingTest] = useState(null)
  const [editingVpn, setEditingVpn] = useState(null)
  const [testFilters, setTestFilters] = useState({
    matchday: '',
    competition: 'Ligue 1',
    broadcaster: '',
    vpn: '',
    dateFilter: { startDate: '', endDate: '' },
  })

  // State for test form
  const [testForm, setTestForm] = useState({
    matchday: '',
    competition: 'Ligue 1',
    broadcaster: '',
    vpn: '',
    testResult: 'Success',
    notes: '',
    testDate: new Date().toISOString().slice(0, 19),
    screenshot: null,
    countryChecked: '',
    countryLocated: '', // NEW FIELD
    frenchCardRegistration: '',
    vpnIpAddress: '',
    ligue1ContentWhileActivated: '',
    ligue1ContentWhileDeactivated: '',
  })

  // State for test loading
  const [addingTest, setAddingTest] = useState(false)
  const [updatingTest, setUpdatingTest] = useState(false)
  const [deletingTests, setDeletingTests] = useState({})

  // State for recognized countries
  const [recognizedCountries, setRecognizedCountries] = useState([])

  // State for matchdays
  const [matchdays, setMatchdays] = useState([])

  // State for screenshot dialog
  const [showScreenshotDialog, setShowScreenshotDialog] = useState(false)
  const [selectedScreenshot, setSelectedScreenshot] = useState(null)

  // State for notifications
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' })

  // Refs for file inputs
  const broadcasterFileRef = useRef()
  const vpnFileRef = useRef()
  const screenshotRef = useRef()
  const vpnLogoRef = useRef()

  // Load existing data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const existingBroadcasters = await getBroadcasters()
        if (existingBroadcasters && existingBroadcasters.length > 0) {
          setBroadcasters(existingBroadcasters)
        }

        const existingVpns = await getVpns()
        if (existingVpns && existingVpns.length > 0) {
          setVpns(existingVpns)
        }

        // Load VPN tests and stats
        const tests = await getVpnTests()
        if (tests && tests.length > 0) {
          setVpnTests(tests)
        }

        const stats = await getVpnTestStats()
        if (stats) {
          setVpnTestStats(stats)
        }

        // Fetch recognized countries for the select box
        const countries = await getRecognizedCountries()
        setRecognizedCountries(countries)
      } catch (error) {
        console.error('Error loading existing data:', error)
      }
    }

    loadData()
  }, [getBroadcasters, getVpns, getVpnTests, getVpnTestStats, getRecognizedCountries, getDistinctMatchdays])

  // Broadcasters Management Handlers
  const handleAddBroadcaster = async () => {
    if (newBroadcaster.trim()) {
      const broadcasterNames = broadcasters.map((b) => (typeof b === 'object' ? b.name : b))
      if (!broadcasterNames.includes(newBroadcaster.trim())) {
        setAddingBroadcaster(true)
        try {
          const result = await addBroadcaster(newBroadcaster.trim())
          if (result) {
            // Add the new broadcaster with ID to the list
            setBroadcasters([...broadcasters, result])
            setNewBroadcaster('')
          }
        } catch (error) {
          console.error('Error adding broadcaster:', error)
        } finally {
          setAddingBroadcaster(false)
        }
      }
    }
  }

  const handleRemoveBroadcaster = async (broadcaster) => {
    if (!broadcaster.id) {
      // If no ID, just remove from local state (for items not yet saved to DB)
      setBroadcasters(broadcasters.filter((b) => b !== broadcaster))
      return
    }

    setDeletingBroadcasters((prev) => ({ ...prev, [broadcaster.id]: true }))

    try {
      await deleteBroadcaster(broadcaster.id)
      // Reload broadcasters from database
      const existingBroadcasters = await getBroadcasters()
      if (existingBroadcasters && existingBroadcasters.length > 0) {
        setBroadcasters(existingBroadcasters)
      } else {
        setBroadcasters([])
      }
    } catch (error) {
      console.error('Error deleting broadcaster:', error)
    } finally {
      setDeletingBroadcasters((prev) => ({ ...prev, [broadcaster.id]: false }))
    }
  }

  // VPNs Management Handlers
  const handleAddVpn = async () => {
    if (newVpn.trim()) {
      if (editingVpn) {
        // Update existing VPN
        setUpdatingVpn(true)
        try {
          const result = await updateVpn(editingVpn.id, newVpn.trim(), vpnLogo, vpnDescription.trim())
          if (result) {
            // Reload VPNs from database
            const existingVpns = await getVpns()
            if (existingVpns && existingVpns.length > 0) {
              setVpns(existingVpns)
            } else {
              setVpns([])
            }
            setNewVpn('')
            setVpnDescription('')
            setVpnLogo(null)
            setEditingVpn(null)
          }
        } catch (error) {
          console.error('Error updating VPN:', error)
        } finally {
          setUpdatingVpn(false)
        }
      } else {
        // Add new VPN
        const vpnNames = vpns.map((v) => (typeof v === 'object' ? v.name : v))
        if (!vpnNames.includes(newVpn.trim())) {
          setAddingVpn(true)
          try {
            const result = await addVpn(newVpn.trim(), vpnLogo, vpnDescription.trim())
            if (result) {
              setVpns([...vpns, result])
              setNewVpn('')
              setVpnDescription('')
              setVpnLogo(null)
            }
          } catch (error) {
            console.error('Error adding VPN:', error)
          } finally {
            setAddingVpn(false)
          }
        }
      }
    }
  }

  const handleRemoveVpn = async (vpn) => {
    if (!vpn.id) {
      // If no ID, just remove from local state (for items not yet saved to DB)
      setVpns(vpns.filter((v) => v !== vpn))
      return
    }

    setDeletingVpns((prev) => ({ ...prev, [vpn.id]: true }))

    try {
      await deleteVpn(vpn.id)
      // Reload VPNs from database
      const existingVpns = await getVpns()
      if (existingVpns && existingVpns.length > 0) {
        setVpns(existingVpns)
      } else {
        setVpns([])
      }
    } catch (error) {
      console.error('Error deleting VPN:', error)
    } finally {
      setDeletingVpns((prev) => ({ ...prev, [vpn.id]: false }))
    }
  }

  const handleEditVpn = (vpn) => {
    setEditingVpn(vpn)
    setNewVpn(vpn.name)
    setVpnDescription(vpn.description || '')
    setVpnLogo(null)
  }

  const handleCancelEdit = () => {
    setEditingVpn(null)
    setNewVpn('')
    setVpnDescription('')
    setVpnLogo(null)
  }

  // File Upload Handlers
  const handleBroadcasterFileUpload = async (event) => {
    const file = event.target.files[0]
    if (file) {
      setBroadcasterFile(file)
      try {
        const result = await uploadFile(file, 'broadcasters')
        if (result && result.names) {
          // Reload broadcasters from database to get full objects with IDs
          const existingBroadcasters = await getBroadcasters()
          if (existingBroadcasters && existingBroadcasters.length > 0) {
            setBroadcasters(existingBroadcasters)
          } else {
            setBroadcasters([])
          }
        }
      } catch (error) {
        console.error('Error uploading broadcaster file:', error)
      }
    }
  }

  const handleVpnFileUpload = async (event) => {
    const file = event.target.files[0]
    if (file) {
      setVpnFile(file)
      try {
        const result = await uploadFile(file, 'vpns')
        if (result && result.names) {
          // Reload VPNs from database to get full objects with IDs
          const existingVpns = await getVpns()
          if (existingVpns && existingVpns.length > 0) {
            setVpns(existingVpns)
          } else {
            setVpns([])
          }
        }
      } catch (error) {
        console.error('Error uploading VPN file:', error)
      }
    }
  }

  // Export data
  const handleExport = (type) => {
    const data = type === 'broadcasters' ? broadcasters : vpns
    const names = data.map((item) => (typeof item === 'object' ? item.name : item))
    const blob = new Blob([names.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${type}_list.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // VPN Test Handlers
  const handleAddTest = async () => {
    if (!testForm.broadcaster || !testForm.vpn || !testForm.competition) {
      alert('Please select broadcaster, VPN, and competition')
      return
    }

    setAddingTest(true)
    try {
      // Prepare form data with proper handling for tinyint fields
      const formData = {
        ...testForm,
        countryChecked: getCountryNameById(testForm.countryChecked),
        countryLocated: getCountryNameById(testForm.countryLocated),
        frenchCardRegistration: testForm.frenchCardRegistration || '',
        ligue1ContentWhileActivated: testForm.ligue1ContentWhileActivated || '',
        ligue1ContentWhileDeactivated: testForm.ligue1ContentWhileDeactivated || '',
      }

      const result = await addVpnTest(formData)
      if (result) {
        // Refetch VPN tests with current filters
        const hasFilters = await refetchVpnTests()

        setShowTestModal(false)
        setTestForm({
          matchday: '',
          competition: 'Ligue 1',
          broadcaster: '',
          vpn: '',
          testResult: 'Success',
          notes: '',
          testDate: new Date().toISOString().slice(0, 16),
          screenshot: null,
          countryChecked: '',
          countryLocated: '',
          frenchCardRegistration: '',
          vpnIpAddress: '',
          ligue1ContentWhileActivated: '',
          ligue1ContentWhileDeactivated: '',
        })

        const message = hasFilters
          ? 'VPN test added successfully! (Note: Item may not be visible due to active filters)'
          : 'VPN test added successfully!'
        showNotification(message)
      }
    } catch (error) {
      console.error('Error adding VPN test:', error)
      showNotification('Failed to add VPN test. Please try again.', 'error')
    } finally {
      setAddingTest(false)
    }
  }

  const handleUpdateTest = async () => {
    if (!editingTest || !testForm.broadcaster || !testForm.vpn || !testForm.competition) {
      alert('Please select broadcaster, VPN, and competition')
      return
    }

    setUpdatingTest(true)
    try {
      // Prepare form data with proper handling for tinyint fields
      const formData = {
        ...testForm,
        countryChecked: getCountryNameById(testForm.countryChecked),
        countryLocated: getCountryNameById(testForm.countryLocated),
        frenchCardRegistration: testForm.frenchCardRegistration || '',
        ligue1ContentWhileActivated: testForm.ligue1ContentWhileActivated || '',
        ligue1ContentWhileDeactivated: testForm.ligue1ContentWhileDeactivated || '',
      }

      const result = await updateVpnTest(editingTest.id, formData)
      if (result) {
        // Refetch VPN tests with current filters
        const hasFilters = await refetchVpnTests()

        setShowTestModal(false)
        setEditingTest(null)
        setTestForm({
          matchday: '',
          competition: '',
          broadcaster: '',
          vpn: '',
          testResult: 'Success',
          notes: '',
          testDate: new Date().toISOString().slice(0, 16),
          screenshot: null,
          countryChecked: '',
          countryLocated: '',
          frenchCardRegistration: '',
          vpnIpAddress: '',
          ligue1ContentWhileActivated: '',
          ligue1ContentWhileDeactivated: '',
        })

        const message = hasFilters
          ? 'VPN test updated successfully! (Note: Item may not be visible due to active filters)'
          : 'VPN test updated successfully!'
        showNotification(message)
      }
    } catch (error) {
      console.error('Error updating VPN test:', error)
      showNotification('Failed to update VPN test. Please try again.', 'error')
    } finally {
      setUpdatingTest(false)
    }
  }

  const handleDeleteTest = async (testId) => {
    setDeletingTests((prev) => ({ ...prev, [testId]: true }))

    try {
      await deleteVpnTest(testId)
      // Refetch VPN tests with current filters
      await refetchVpnTests()
      showNotification('VPN test deleted successfully!')
    } catch (error) {
      console.error('Error deleting VPN test:', error)
      showNotification('Failed to delete VPN test. Please try again.', 'error')
    } finally {
      setDeletingTests((prev) => ({ ...prev, [testId]: false }))
    }
  }

  const handleEditTest = async (test) => {
    setEditingTest(test)

    // Convert database integer values back to strings for form display
    const convertFromTinyint = (value) => {
      if (value === 1) return 'yes'
      if (value === 0) return ''
      return ''
    }

    // Helper function to get country ID by name
    const getCountryIdByName = (countryName) => {
      if (!countryName) return ''
      const country = recognizedCountries.find((c) => c.name === countryName)
      return country ? country.id : ''
    }

    setTestForm({
      matchday: test.matchday || '',
      competition: test.competition || '',
      broadcaster: test.broadcaster_name || '',
      vpn: test.vpn_name || '',
      testResult: test.test_result || 'Success',
      notes: test.notes || '',
      testDate: test.test_date ? test.test_date.slice(0, 16) : new Date().toISOString().slice(0, 16),
      screenshot: null,
      countryChecked: getCountryIdByName(test.country_checked),
      countryLocated: getCountryIdByName(test.country_located),
      frenchCardRegistration: convertFromTinyint(test.french_card_registration),
      vpnIpAddress: test.vpn_ip_address || '',
      ligue1ContentWhileActivated: convertFromTinyint(test.ligue1_content_while_activated),
      ligue1ContentWhileDeactivated: convertFromTinyint(test.ligue1_content_while_deactivated),
    })

    // Fetch matchdays if competition is selected
    if (test.competition) {
      try {
        const matchdaysData = await getDistinctMatchdays(test.competition.split(' ')[1])
        if (matchdaysData && matchdaysData.length > 0) {
          setMatchdays(matchdaysData)
        } else {
          setMatchdays([])
        }
      } catch (error) {
        console.error('Error fetching matchdays:', error)
        setMatchdays([])
      }
    }

    setShowTestModal(true)
  }

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type })
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000)
  }

  // Helper function to clean filters by removing empty values
  const getCleanFilters = (filters) => {
    const clean = {}

    if (filters.matchday && filters.matchday.trim()) {
      clean.matchday = filters.matchday.trim()
    }
    if (filters.competition && filters.competition.trim()) {
      clean.competition = filters.competition.trim()
    }
    if (filters.broadcaster && filters.broadcaster.trim()) {
      clean.broadcaster = filters.broadcaster.trim()
    }
    if (filters.vpn && filters.vpn.trim()) {
      clean.vpn = filters.vpn.trim()
    }
    if (filters.dateFilter && filters.dateFilter.startDate && filters.dateFilter.startDate.trim()) {
      clean.dateFilter = clean.dateFilter || {}
      clean.dateFilter.startDate = filters.dateFilter.startDate.trim()
    }
    if (filters.dateFilter && filters.dateFilter.endDate && filters.dateFilter.endDate.trim()) {
      clean.dateFilter = clean.dateFilter || {}
      clean.dateFilter.endDate = filters.dateFilter.endDate.trim()
    }

    return clean
  }

  // Helper function to refetch VPN tests with current filters
  const refetchVpnTests = async () => {
    try {
      // Create a clean filter object with only non-empty values
      const cleanFilters = getCleanFilters(testFilters)

      // Check if any filters are applied
      const hasFilters = Object.keys(cleanFilters).length > 0

      const tests = await getVpnTests(hasFilters ? cleanFilters : undefined)
      if (tests) {
        setVpnTests(tests)
      }

      const stats = await getVpnTestStats(hasFilters ? cleanFilters : undefined)
      if (stats) {
        setVpnTestStats(stats)
      }

      // Return whether filters are applied for potential notification
      return hasFilters
    } catch (error) {
      console.error('Error refetching VPN tests:', error)
      return false
    }
  }

  const handleFilterTests = async () => {
    try {
      // Create a clean filter object with only non-empty values
      const cleanFilters = getCleanFilters(testFilters)

      const tests = await getVpnTests(cleanFilters)
      if (tests) {
        setVpnTests(tests)
      }

      const stats = await getVpnTestStats(cleanFilters)
      if (stats) {
        setVpnTestStats(stats)
      }
    } catch (error) {
      console.error('Error filtering tests:', error)
    }
  }

  const handleScreenshotChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      setTestForm((prev) => ({ ...prev, screenshot: file }))
    }
  }

  const handleScreenshotClick = (screenshotPath) => {
    if (screenshotPath) {
      setSelectedScreenshot(`${process.env.REACT_APP_STATIC_URL}${screenshotPath}`)
      setShowScreenshotDialog(true)
    }
  }

  // Handle competition change to fetch matchdays
  const handleCompetitionChange = async (competition) => {
    setTestForm((prev) => ({ ...prev, competition, matchday: '' }))

    if (competition) {
      try {
        const matchdaysData = await getDistinctMatchdays(competition.split(' ')[1])
        if (matchdaysData && matchdaysData.length > 0) {
          setMatchdays(matchdaysData)
        } else {
          setMatchdays([])
        }
      } catch (error) {
        console.error('Error fetching matchdays:', error)
        setMatchdays([])
      }
    } else {
      setMatchdays([])
    }
  }

  // Helper function to get country name by ID
  const getCountryNameById = (countryId) => {
    if (!countryId) return ''
    const country = recognizedCountries.find((c) => c.id == countryId)
    return country ? country.name : ''
  }

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'N/A'

      // Format with date, hours, minutes, and seconds
      const dateStr = date.toLocaleDateString()
      const timeStr = date.toLocaleTimeString('en-US', {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
      return `${dateStr} ${timeStr}`
    } catch (error) {
      return 'N/A'
    }
  }

  const getResultColor = (result) => {
    switch (result) {
      case 'Success':
        return 'text-green-600 bg-green-100'
      case 'Blocked':
        return 'text-red-600 bg-red-100'
      case 'Failed':
        return 'text-yellow-600 bg-yellow-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <AppLayout pageName='VPN'>
      <div className='container mx-auto px-4 py-6'>
        <div className='w-full mx-auto'>
          {/* Header */}
          <div className='text-center mb-8'>
            <div className='inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4'>
              <FaShieldAlt className='w-8 h-8 text-blue-600' />
            </div>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>Add Broadcasters and VPNs</h1>
            <p className='text-gray-600'>Manage and organize broadcasters and VPNs for testing purposes</p>
          </div>

          {/* Notification */}
          {notification.show && (
            <div
              className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
                notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}
            >
              <div className='flex items-center'>
                {notification.type === 'success' ? <FaCheck className='mr-2' /> : <FaXmark className='mr-2' />}
                {notification.message}
              </div>
            </div>
          )}

          {/* Management Panel */}
          <div className='bg-white rounded-lg shadow-lg p-6 mb-6'>
            <div className='flex items-center justify-between mb-6'>
              <h2 className='text-2xl font-bold text-gray-900'>Broadcasters & VPNs Management</h2>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
              {/* Broadcasters Section */}
              <div className='bg-gray-50 rounded-lg p-6'>
                <div className='flex items-center justify-between mb-6'>
                  <div className='flex items-center'>
                    <FaTv className='w-6 h-6 text-blue-600 mr-3' />
                    <h3 className='text-xl font-semibold text-gray-900'>Broadcasters</h3>
                  </div>
                  <span className='bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full'>
                    {broadcasters.length} items
                  </span>
                </div>

                {/* Manual Input */}
                <div className='mb-6'>
                  <h4 className='text-lg font-medium text-gray-900 mb-3'>Add Manually</h4>
                  <div className='flex gap-2'>
                    <input
                      type='text'
                      value={newBroadcaster}
                      onChange={(e) => setNewBroadcaster(e.target.value)}
                      placeholder='Enter broadcaster name (e.g., beIN Sports)'
                      className='flex-1 form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                      onKeyPress={(e) => e.key === 'Enter' && handleAddBroadcaster()}
                    />
                    <button
                      onClick={handleAddBroadcaster}
                      disabled={!newBroadcaster.trim() || addingBroadcaster}
                      className='btn-submit'
                    >
                      {addingBroadcaster ? (
                        <Bounce animating={addingBroadcaster} />
                      ) : (
                        <>
                          <FaPlus className='mr-2' />
                          Add
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* File Upload */}
                <div className='mb-6'>
                  <h4 className='text-lg font-medium text-gray-900 mb-3'>Upload from File</h4>
                  <div className='flex gap-2 mb-3'>
                    <select
                      value={uploadFormat}
                      onChange={(e) => setUploadFormat(e.target.value)}
                      className='form-control'
                    >
                      <option value='excel'>Excel (.xlsx)</option>
                      <option value='pdf'>PDF</option>
                    </select>
                    <button
                      onClick={() => {
                        broadcasterFileRef.current.click()
                      }}
                      className='btn-submit'
                    >
                      <FaUpload className='mr-2' />
                      Upload
                    </button>
                  </div>
                  <input
                    ref={broadcasterFileRef}
                    type='file'
                    accept={uploadFormat === 'excel' ? '.xlsx,.xls' : '.pdf'}
                    onChange={handleBroadcasterFileUpload}
                    className='hidden'
                  />
                  {broadcasterFile && (
                    <div className='flex items-center text-sm text-green-600'>
                      <FaCheck className='mr-2' />
                      {broadcasterFile.name} uploaded successfully
                    </div>
                  )}
                </div>

                {/* Broadcasters List */}
                <div className='mb-6'>
                  <h4 className='text-lg font-medium text-gray-900 mb-3'>Current Broadcasters</h4>
                  <div className='max-h-60 overflow-y-auto border rounded-lg bg-white'>
                    {broadcasters.length === 0 ? (
                      <div className='p-4 text-center text-gray-500'>No broadcasters added yet</div>
                    ) : (
                      <div className='divide-y'>
                        {broadcasters.map((broadcaster, index) => {
                          const isDeleting =
                            typeof broadcaster === 'object' && broadcaster.id
                              ? deletingBroadcasters[broadcaster.id]
                              : false
                          const broadcasterName = typeof broadcaster === 'object' ? broadcaster.name : broadcaster
                          const broadcasterId = typeof broadcaster === 'object' ? broadcaster.id : null

                          return (
                            <div
                              key={index}
                              className='flex items-center justify-between p-3 hover:bg-gray-50'
                            >
                              <span className='text-gray-900'>{broadcasterName}</span>
                              <button
                                onClick={() => handleRemoveBroadcaster(broadcaster)}
                                disabled={isDeleting}
                                className={`text-red-500 hover:text-red-700 ${
                                  isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {isDeleting ? (
                                  <Bounce
                                    animating={true}
                                    size={16}
                                  />
                                ) : (
                                  <FaTrash />
                                )}
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* VPNs Section */}
              <div className='bg-gray-50 rounded-lg p-6'>
                <div className='flex items-center justify-between mb-6'>
                  <div className='flex items-center'>
                    <FaShieldAlt className='w-6 h-6 text-purple-600 mr-3' />
                    <h3 className='text-xl font-semibold text-gray-900'>VPNs</h3>
                  </div>
                  <span className='bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full'>
                    {vpns.length} items
                  </span>
                </div>

                {/* Manual Input */}
                <div className='mb-6'>
                  <h4 className='text-lg font-medium text-gray-900 mb-3'>{editingVpn ? 'Edit VPN' : 'Add Manually'}</h4>
                  <div className='space-y-3'>
                    <div className='flex gap-2'>
                      <input
                        ref={vpnLogoRef}
                        type='file'
                        accept='image/*'
                        style={{ display: 'none' }}
                        onChange={(e) => setVpnLogo(e.target.files[0])}
                      />
                      <button
                        onClick={() => vpnLogoRef.current.click()}
                        className='btn-submit'
                        type='button'
                        title={editingVpn ? 'Upload New Logo' : 'Upload VPN Logo'}
                      >
                        <FaImage />
                      </button>
                      <input
                        type='text'
                        value={newVpn}
                        onChange={(e) => setNewVpn(e.target.value)}
                        placeholder={editingVpn ? 'Edit VPN name' : 'Enter VPN name (e.g., NordVPN)'}
                        className='flex-1 form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                        onKeyPress={(e) => e.key === 'Enter' && handleAddVpn()}
                      />
                      <button
                        onClick={handleAddVpn}
                        disabled={!newVpn.trim() || addingVpn || updatingVpn}
                        className='btn-submit'
                      >
                        {addingVpn || updatingVpn ? (
                          <Bounce animating={addingVpn || updatingVpn} />
                        ) : editingVpn ? (
                          <>
                            <FaEdit className='mr-2' />
                            Update
                          </>
                        ) : (
                          <>
                            <FaPlus className='mr-2' />
                            Add
                          </>
                        )}
                      </button>
                      {editingVpn && (
                        <button
                          onClick={handleCancelEdit}
                          className='btn-secondary'
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    <textarea
                      value={vpnDescription}
                      onChange={(e) => setVpnDescription(e.target.value)}
                      placeholder={editingVpn ? 'Edit VPN description' : 'Enter VPN description (optional)'}
                      rows='3'
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    />
                  </div>
                  {vpnLogo && (
                    <div className='flex items-center text-sm text-green-600 mt-2'>
                      <FaCheck className='mr-2' />
                      {vpnLogo.name} selected
                    </div>
                  )}
                  {editingVpn && editingVpn.logo_path && !vpnLogo && (
                    <div className='flex items-center text-sm text-blue-600 mt-2'>
                      <FaImage className='mr-2' />
                      Current logo: {editingVpn.logo_path.split('/').pop()}
                    </div>
                  )}
                </div>

                {/* File Upload */}
                <div className='mb-6'>
                  <h4 className='text-lg font-medium text-gray-900 mb-3'>Upload from File</h4>
                  <div className='flex gap-2 mb-3'>
                    <select
                      value={uploadFormat}
                      onChange={(e) => setUploadFormat(e.target.value)}
                      className='form-control'
                    >
                      <option value='excel'>Excel (.xlsx)</option>
                      <option value='pdf'>PDF</option>
                    </select>
                    <button
                      onClick={() => {
                        vpnFileRef.current.click()
                      }}
                      className='btn-submit'
                    >
                      <FaUpload className='mr-2' />
                      Upload
                    </button>
                  </div>
                  <input
                    ref={vpnFileRef}
                    type='file'
                    accept={uploadFormat === 'excel' ? '.xlsx,.xls' : '.pdf'}
                    onChange={handleVpnFileUpload}
                    className='hidden'
                  />
                  {vpnFile && (
                    <div className='flex items-center text-sm text-green-600'>
                      <FaCheck className='mr-2' />
                      {vpnFile.name} uploaded successfully
                    </div>
                  )}
                </div>

                {/* VPNs List */}
                <div className='mb-6'>
                  <h4 className='text-lg font-medium text-gray-900 mb-3'>Current VPNs</h4>
                  <div className='max-h-60 overflow-y-auto border rounded-lg bg-white'>
                    {vpns.length === 0 ? (
                      <div className='p-4 text-center text-gray-500'>No VPNs added yet</div>
                    ) : (
                      <div className='divide-y'>
                        {vpns.map((vpn, index) => {
                          const isDeleting = typeof vpn === 'object' && vpn.id ? deletingVpns[vpn.id] : false
                          const vpnName = typeof vpn === 'object' ? vpn.name : vpn
                          const vpnId = typeof vpn === 'object' ? vpn.id : null
                          const vpnLogoPath = typeof vpn === 'object' ? vpn.logo_path : null
                          const vpnDescription = typeof vpn === 'object' ? vpn.description : null

                          return (
                            <div
                              key={index}
                              className='p-3 hover:bg-gray-50'
                            >
                              <div className='flex items-center justify-between mb-2'>
                                <span className='flex items-center gap-2 text-gray-900 font-medium'>
                                  {vpnLogoPath && (
                                    <img
                                      src={getVpnImage(vpnLogoPath)}
                                      alt={vpnName}
                                      className='w-6 h-6 object-contain rounded'
                                    />
                                  )}
                                  {vpnName}
                                </span>
                                <div className='flex space-x-2'>
                                  <button
                                    onClick={() => handleEditVpn(vpn)}
                                    className='text-blue-600 hover:text-blue-800'
                                    title='Edit VPN'
                                  >
                                    <FaEdit className='w-4 h-4' />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveVpn(vpn)}
                                    disabled={isDeleting}
                                    className={`text-red-500 hover:text-red-700 ${
                                      isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                    title='Delete VPN'
                                  >
                                    {isDeleting ? (
                                      <Bounce
                                        animating={true}
                                        size={16}
                                      />
                                    ) : (
                                      <FaTrash />
                                    )}
                                  </button>
                                </div>
                              </div>
                              {vpnDescription && <p className='text-sm text-gray-600 text-left'>{vpnDescription}</p>}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* VPN Test Management Section */}
          <div className='bg-white rounded-lg shadow-lg p-6 mb-6'>
            <div className='flex items-center justify-between mb-6'>
              <div className='flex items-center'>
                <FaChartBar className='w-6 h-6 text-indigo-600 mr-3' />
                <h2 className='text-2xl font-bold text-gray-900'>VPN Test Management</h2>
              </div>
              <button
                onClick={() => setShowTestModal(true)}
                className='btn-submit bg-indigo-600 hover:bg-indigo-700 border-indigo-600'
              >
                <FaPlus className='mr-2' />
                Add New Test
              </button>
            </div>

            {/* Stats Overview */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
              <div className='bg-blue-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaChartBar className='w-5 h-5 text-blue-600 mr-2' />
                  <div>
                    <p className='text-sm text-blue-600'>Total Tests</p>
                    <p className='text-2xl font-bold text-blue-900'>{vpnTestStats.total_tests || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-green-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaCheck className='w-5 h-5 text-green-600 mr-2' />
                  <div>
                    <p className='text-sm text-green-600'>Successful</p>
                    <p className='text-2xl font-bold text-green-900'>{vpnTestStats.successful_tests || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-red-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaTrash className='w-5 h-5 text-red-600 mr-2' />
                  <div>
                    <p className='text-sm text-red-600'>Blocked</p>
                    <p className='text-2xl font-bold text-red-900'>{vpnTestStats.blocked_tests || 0}</p>
                  </div>
                </div>
              </div>
              <div className='bg-yellow-50 rounded-lg p-4'>
                <div className='flex items-center'>
                  <FaCog className='w-5 h-5 text-yellow-600 mr-2' />
                  <div>
                    <p className='text-sm text-yellow-600'>Failed</p>
                    <p className='text-2xl font-bold text-yellow-900'>{vpnTestStats.failed_tests || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className='bg-gray-50 rounded-lg p-4 mb-6'>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center'>
                  <FaFilter className='w-5 h-5 text-gray-600 mr-2' />
                  <h3 className='text-lg font-semibold text-gray-900'>Filters</h3>
                </div>
                {(testFilters.matchday ||
                  testFilters.competition ||
                  testFilters.broadcaster ||
                  testFilters.vpn ||
                  testFilters.dateFilter.startDate ||
                  testFilters.dateFilter.endDate) && (
                  <span className='bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full'>
                    Active Filters
                  </span>
                )}
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                <input
                  type='text'
                  placeholder='Matchday'
                  value={testFilters.matchday}
                  onChange={(e) => setTestFilters((prev) => ({ ...prev, matchday: e.target.value }))}
                  className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                />
                <input
                  type='text'
                  placeholder='Competition'
                  value={'Ligue 1'}
                  disabled
                  onChange={(e) => setTestFilters((prev) => ({ ...prev, competition: e.target.value }))}
                  className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                />
                <select
                  value={testFilters.broadcaster}
                  onChange={(e) => setTestFilters((prev) => ({ ...prev, broadcaster: e.target.value }))}
                  className='form-control'
                >
                  <option value=''>All Broadcasters</option>
                  {broadcasters.map((broadcaster) => (
                    <option
                      key={broadcaster.id}
                      value={broadcaster.name}
                    >
                      {broadcaster.name}
                    </option>
                  ))}
                </select>
                <select
                  value={testFilters.vpn}
                  onChange={(e) => setTestFilters((prev) => ({ ...prev, vpn: e.target.value }))}
                  className='form-control'
                >
                  <option value=''>All VPNs</option>
                  {vpns.map((vpn) => (
                    <option
                      key={vpn.id}
                      value={vpn.name}
                    >
                      {vpn.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
                <input
                  type='date'
                  placeholder='Start Date'
                  value={testFilters.dateFilter.startDate}
                  onChange={(e) =>
                    setTestFilters((prev) => ({
                      ...prev,
                      dateFilter: { ...prev.dateFilter, startDate: e.target.value },
                    }))
                  }
                  className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                />
                <input
                  type='date'
                  placeholder='End Date'
                  value={testFilters.dateFilter.endDate}
                  onChange={(e) =>
                    setTestFilters((prev) => ({
                      ...prev,
                      dateFilter: { ...prev.dateFilter, endDate: e.target.value },
                    }))
                  }
                  className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                />
              </div>
              <div className='mt-4 flex gap-2'>
                <button
                  onClick={handleFilterTests}
                  className='btn-submit bg-gray-600 hover:bg-gray-700 border-gray-600'
                >
                  <FaFilter className='mr-2' />
                  Apply Filters
                </button>
                <button
                  onClick={() => {
                    setTestFilters({
                      matchday: '',
                      competition: 'Ligue 1',
                      broadcaster: '',
                      vpn: '',
                      dateFilter: { startDate: '', endDate: '' },
                    })
                    refetchVpnTests()
                  }}
                  className='btn-secondary'
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Tests Table */}
            <div className='overflow-x-auto'>
              <table className='min-w-full bg-white border border-gray-200 rounded-lg'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Date
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Matchday
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Competition
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Broadcaster
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      VPN
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Result
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Screenshot
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200'>
                  {vpnTests.length === 0 ? (
                    <tr>
                      <td
                        colSpan='8'
                        className='px-4 py-8 text-center text-gray-500'
                      >
                        No VPN tests found
                      </td>
                    </tr>
                  ) : (
                    vpnTests.map((test) => (
                      <tr
                        key={test.id}
                        className='hover:bg-gray-50'
                      >
                        <td className='px-4 py-3 text-sm text-gray-900'>
                          {formatDate(test.test_date)}
                        </td>
                        <td className='px-4 py-3 text-sm text-gray-900'>{test.matchday || '-'}</td>
                        <td className='px-4 py-3 text-sm text-gray-900'>{test.competition || '-'}</td>
                        <td className='px-4 py-3 text-sm text-gray-900'>{test.broadcaster_name || '-'}</td>
                        <td className='px-4 py-3 text-sm text-gray-900'>{test.vpn_name || '-'}</td>
                        <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getResultColor(test.test_result)}`}
                          >
                            {test.test_result}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-sm text-gray-900'>
                          {test.screenshot_path ? (
                            <button
                              onClick={() => handleScreenshotClick(test.screenshot_path)}
                              className='text-blue-600 hover:text-blue-800 cursor-pointer'
                            >
                              <FaImage className='w-4 h-4' />
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className='px-4 py-3 text-sm text-gray-900'>
                          <div className='flex space-x-2'>
                            <button
                              onClick={() => handleEditTest(test)}
                              className='text-blue-600 hover:text-blue-800'
                            >
                              <FaEdit className='w-4 h-4' />
                            </button>
                            <button
                              onClick={() => handleDeleteTest(test.id)}
                              disabled={deletingTests[test.id]}
                              className={`text-red-600 hover:text-red-800 ${
                                deletingTests[test.id] ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              {deletingTests[test.id] ? (
                                <Bounce
                                  animating={true}
                                  size={16}
                                />
                              ) : (
                                <FaTrash className='w-4 h-4' />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Test Modal */}
          {showTestModal && (
            <div className='fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50'>
              <div className='bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>
                    {editingTest ? 'Edit VPN Test' : 'Add New VPN Test'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowTestModal(false)
                      setEditingTest(null)
                      setTestForm({
                        matchday: '',
                        competition: 'Ligue 1',
                        broadcaster: '',
                        vpn: '',
                        testResult: 'Success',
                        notes: '',
                        testDate: new Date().toISOString().slice(0, 16),
                        screenshot: null,
                        countryChecked: '',
                        countryLocated: '',
                        frenchCardRegistration: '',
                        vpnIpAddress: '',
                        ligue1ContentWhileActivated: '',
                        ligue1ContentWhileDeactivated: '',
                      })
                    }}
                    className='text-gray-400 hover:text-gray-600'
                  >
                    <FaXmark className='w-5 h-5' />
                  </button>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                  {/* Competition */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>Competition</label>
                    <input
                      type='text'
                      value={'Ligue 1'}
                      disabled
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2 user-select-none'
                    />
                    {/* <select
                      value={testForm.competition}
                      onChange={(e) => handleCompetitionChange(e.target.value)}
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    >
                      <option value=''>Select Competition</option>
                      <option value='Ligue 1'>Ligue 1</option>
                      <option value='Ligue 2'>Ligue 2</option>
                    </select> */}
                  </div>
                  {/* Broadcaster */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>Broadcaster</label>
                    <select
                      value={testForm.broadcaster}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, broadcaster: e.target.value }))}
                      className='w-full form-control'
                    >
                      <option value=''>Select Broadcaster</option>
                      {broadcasters.map((broadcaster) => (
                        <option
                          key={broadcaster.id}
                          value={broadcaster.name}
                        >
                          {broadcaster.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* VPN */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>VPN</label>
                    <select
                      value={testForm.vpn}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, vpn: e.target.value }))}
                      className='w-full form-control'
                    >
                      <option value=''>Select VPN</option>
                      {vpns.map((vpn) => (
                        <option
                          key={vpn.id}
                          value={vpn.name}
                        >
                          {vpn.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Test Result */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>Test Result</label>
                    <select
                      value={testForm.testResult}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, testResult: e.target.value }))}
                      className='w-full form-control'
                    >
                      <option value='Success'>Success</option>
                      <option value='Blocked'>Blocked</option>
                      <option value='Failed'>Failed</option>
                    </select>
                  </div>
                  {/* Ligue 1 Content While Activated */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      While the VPN was activated, were you able to successfully watch Ligue 1 content or see
                      advertisements for the Ligue?
                    </label>
                    <select
                      value={testForm.ligue1ContentWhileActivated}
                      onChange={(e) =>
                        setTestForm((prev) => ({ ...prev, ligue1ContentWhileActivated: e.target.value }))
                      }
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    >
                      <option value=''>N/A</option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </select>
                  </div>
                  {/* Ligue 1 Content While Deactivated */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      While the VPN was deactivated, were you able to successfully watch Ligue 1 content or see
                      advertisements for the Ligue?
                    </label>
                    <select
                      value={testForm.ligue1ContentWhileDeactivated}
                      onChange={(e) =>
                        setTestForm((prev) => ({ ...prev, ligue1ContentWhileDeactivated: e.target.value }))
                      }
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    >
                      <option value=''>N/A</option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </select>
                  </div>
                  {/* Country Checked */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      For which country did you check?
                    </label>
                    <select
                      value={testForm.countryChecked}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, countryChecked: e.target.value }))}
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    >
                      <option value=''>N/A</option>
                      {recognizedCountries.map((country) => (
                        <option
                          key={country.id}
                          value={country.id}
                        >
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Country Located */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      In which country are you located?
                    </label>
                    <select
                      value={testForm.countryLocated}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, countryLocated: e.target.value }))}
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    >
                      <option value=''>N/A</option>
                      {recognizedCountries.map((country) => (
                        <option
                          key={country.id}
                          value={country.id}
                        >
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* VPN IP Address */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Please add a VPN IP address if you were able to stream successfully.
                    </label>
                    <input
                      type='text'
                      value={testForm.vpnIpAddress}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, vpnIpAddress: e.target.value }))}
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                      placeholder='Enter VPN IP address'
                    />
                  </div>
                  {/* Matchday */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>Matchday</label>
                    <input
                      type='text'
                      value={testForm.matchday}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, matchday: e.target.value }))}
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                      placeholder='Enter matchday'
                      onKeyDown={(e) => {
                        // Allow only numbers, backspace, delete, arrow keys
                        if (
                          !/^\d$/.test(e.key) &&
                          !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)
                        ) {
                          e.preventDefault()
                          return
                        }

                        if (e.key === 'Enter') {
                          handleAddTest()
                        }
                      }}
                    />
                  </div>
                  {/* Test Date & Time */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>Test Date & Time</label>
                    <input
                      type='datetime-local'
                      step='1'
                      value={testForm.testDate}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, testDate: e.target.value }))}
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    />
                  </div>
                  {/* French Card Registration */}
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      While the VPN was activated, were you able to successfully register with the broadcaster using a
                      French credit or debit card?
                    </label>
                    <select
                      value={testForm.frenchCardRegistration}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, frenchCardRegistration: e.target.value }))}
                      className='w-full form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                    >
                      <option value=''>N/A</option>
                      <option value='yes'>Yes</option>
                      <option value='no'>No</option>
                    </select>
                  </div>
                  {/* Notes (always last) */}
                  <div className='md:col-span-2'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>Notes</label>
                    <textarea
                      value={testForm.notes}
                      onChange={(e) => setTestForm((prev) => ({ ...prev, notes: e.target.value }))}
                      placeholder='Optional notes (error codes, region info, etc.)'
                      rows='3'
                      className='w-full form-control'
                    />
                  </div>
                </div>

                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Screenshot</label>
                  <input
                    ref={screenshotRef}
                    type='file'
                    accept='image/*'
                    onChange={handleScreenshotChange}
                    className='form-control border-2 border-gray-200 focus:border-blue-500 px-4 py-2'
                  />
                </div>

                <div className='flex justify-end space-x-3'>
                  <button
                    onClick={() => {
                      setShowTestModal(false)
                      setEditingTest(null)
                      setTestForm({
                        matchday: '',
                        competition: '',
                        broadcaster: '',
                        vpn: '',
                        testResult: 'Success',
                        notes: '',
                        testDate: new Date().toISOString().slice(0, 16),
                        screenshot: null,
                        countryChecked: '',
                        countryLocated: '',
                        frenchCardRegistration: '',
                        vpnIpAddress: '',
                        ligue1ContentWhileActivated: '',
                        ligue1ContentWhileDeactivated: '',
                      })
                    }}
                    className='btn-secondary'
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingTest ? handleUpdateTest : handleAddTest}
                    disabled={addingTest || updatingTest}
                    className='btn-submit'
                  >
                    {addingTest || updatingTest ? (
                      <Bounce animating={true} />
                    ) : editingTest ? (
                      'Update Test'
                    ) : (
                      'Add Test'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Screenshot Dialog */}
          {showScreenshotDialog && (
            <div className='fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 animate-in fade-in duration-300'>
              <div className='bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-xl font-bold text-gray-900'>Screenshot</h3>
                  <button
                    onClick={() => {
                      setShowScreenshotDialog(false)
                      setSelectedScreenshot(null)
                    }}
                    className='text-gray-400 hover:text-gray-600'
                  >
                    <FaXmark className='w-5 h-5' />
                  </button>
                </div>

                <div className='flex justify-center'>
                  <img
                    src={selectedScreenshot}
                    alt='Screenshot'
                    className='max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg'
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                  <div
                    className='hidden max-w-full max-h-[70vh] flex items-center justify-center bg-gray-100 rounded-lg p-8'
                    style={{ display: 'none' }}
                  >
                    <div className='text-center text-gray-500'>
                      <FaImage className='w-16 h-16 mx-auto mb-4 text-gray-300' />
                      <p className='text-lg font-medium'>Image not available</p>
                      <p className='text-sm'>The screenshot could not be loaded</p>
                    </div>
                  </div>
                </div>

                <div className='flex justify-end mt-6'>
                  <button
                    onClick={() => {
                      setShowScreenshotDialog(false)
                      setSelectedScreenshot(null)
                    }}
                    className='btn-secondary'
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

export default VPN
